import {Injectable} from "@angular/core";
import {HttpClient, HttpErrorResponse, HttpHeaders} from "@angular/common/http";
import {catchError, mergeMap, Observable, of, Subject, throwError} from "rxjs";
import {UserTokensRepository} from "./user-tokens.repository";
import {AppEventBus} from "../event-buses/app.event-bus";
import {AppEvent} from "../event-buses/AppEvent";
import {AppEventName} from "../event-buses/AppEventName";

@Injectable({
  providedIn: 'root',
})
export class HttpRepository          {

  constructor(private httpClient: HttpClient,
              private userTokensRepository: UserTokensRepository,
              private appEventBus: AppEventBus) {

  }

  get<T>(endpoint: string, customHeaders: any = null): Observable<T> {
    return this.httpClient.get<T>(endpoint, this.getOptions('application/json', customHeaders)).pipe(
      catchError((error) => {
        return this.userTokensRepository.getAccessToken().pipe(
          catchError((error2) => {
            this.checkAndHandleSessionExpiration(error2);
            return throwError(error2 || 'Server error');
          }),
          mergeMap((tokenData) => {
            return this.httpClient.get<T>(endpoint, this.getOptions('application/json', customHeaders)).pipe(
              catchError((error3) => {
                this.checkAndHandleSessionExpiration(error3);
                return throwError(error3 || 'Server error');
              })
            );
          })
        );
      })
    );
  }

  put<T>(endpoint: string, body: any, customHeaders: any = null): Observable<T> {
    if(!body) {
      body = '';
    }
    else if(!(typeof body === 'string')) {
      body = JSON.stringify(body);
    }

    return this.httpClient.put<T>(endpoint, body, this.getOptions('application/json', customHeaders)).pipe(
      catchError((error) => {
        return this.userTokensRepository.getAccessToken().pipe(
          catchError((error2) => {
            this.checkAndHandleSessionExpiration(error2);
            return throwError(error2 || 'Server error');
          }),
          mergeMap((tokenData) => {
            return this.httpClient.put<T>(endpoint, body, this.getOptions('application/json', customHeaders)).pipe(
              catchError((error3) => {
                this.checkAndHandleSessionExpiration(error3);
                return throwError(error3 || 'Server error');
              })
            );
          })
        );
      })
    );
  }

  post<T>(endpoint: string, body: any, customHeaders: any = null, streamingFileUpload: boolean = false): Observable<T> {
    if(!body) {
      body = '';
    }
    else if(!(typeof body === 'string') && !streamingFileUpload) {
      body = JSON.stringify(body);
    }

    return this.httpClient.post<T>(endpoint, body, this.getOptions('application/json', customHeaders)).pipe(
      catchError((error) => {
        return this.userTokensRepository.getAccessToken().pipe(
          catchError((error2) => {
            this.checkAndHandleSessionExpiration(error2);
            return throwError(error2 || 'Server error');
          }),
          mergeMap((tokenData) => {
            return this.httpClient.post<T>(endpoint, body, this.getOptions('application/json', customHeaders)).pipe(
              catchError((error3) => {
                this.checkAndHandleSessionExpiration(error3);
                return throwError(error3 || 'Server error');
              })
            );
          })
        );
      })
    );
  }

  delete<T>(endpoint: string, body: any = null, customHeaders: any = null): Observable<T> {
    return this.httpClient.delete<T>(endpoint, this.getOptions('application/json', customHeaders, body)).pipe(
      catchError((error) => {
        return this.userTokensRepository.getAccessToken().pipe(
          catchError((error2) => {
            this.checkAndHandleSessionExpiration(error2);
            return throwError(error2 || 'Server error');
          }),
          mergeMap((tokenData) => {
            return this.httpClient.delete<T>(endpoint, this.getOptions('application/json', customHeaders, body)).pipe(
              catchError((error3) => {
                this.checkAndHandleSessionExpiration(error3);
                return throwError(error3 || 'Server error');
              })
            );
          })
        );
      })
    );
  }

  private getOptions(contentType = 'application/json', customHeaders: any = null, body: any = null) {
    let headers = new HttpHeaders({'Content-Type': contentType});

    const userTokens = this.userTokensRepository.getUserTokens();
    if(userTokens && userTokens.accessToken) {
      headers = headers.set('Authorization', `Bearer ${userTokens.accessToken}`);
    }
    else {
      headers = headers.set('Authorization', 'Bearer __invalid__');
    }

    if(customHeaders) {
      const keys = Object.keys(customHeaders);
      keys.forEach((key) => {
        headers = headers.set(key, customHeaders[key]);
      });
    }

    if(body) {
      return {
        headers: headers,
        body: body
      }
    }
    else {
      return {headers: headers};
    }
  }

  private checkAndHandleSessionExpiration(error: HttpErrorResponse) {
    if(error.status === 401) {
      this.appEventBus.dispatch(new AppEvent(AppEventName.SessionExpiration, {}));
    }
  }
}
